import { cloneDeep, isEqual } from 'lodash'
import { EVENT_BASKET_UPDATED } from '@/config/events'

import {
  BasketHelper,
  DateTimeHelper,
  LogHelper,
  PersonHelper, ProductHelper
} from '@/helpers'

import Vue from '@/setup'

export default {
  namespaced: true,

  state () {
    return {
      address: {},
      basketId: null,
      language: null,
      persons: []
    }
  },

  getters: {
    getApplicationQuestions: (_state, getters) => personId => getters.getPerson(personId).applicationQuestions
      .map(aQuestions => {
        aQuestions.questions.sort((a, b) => (a.order > b.order) ? 1 : -1)
        return aQuestions
      })
      .sort((a, b) => (a.order > b.order) ? 1 : -1),

    /**
     * get categories relevant for passed personId
     * @param  {String} personId
     * @return {Array}
     */
    getCategories: (_state, getters, _rootState, rootGetters) => personId => {
      const categories = getters.getPerson(personId)?.products?.categories ?? {}

      if (!categories) return []

      return rootGetters['products/getCategories']
        .filter(category => !!categories[category.id])
        .map(category => Object.assign(
          category,
          categories[category.id]
        ))
        .sort((a, b) => (a.order > b.order) ? 1 : -1)
    },

    getCategory: (_state, getters) => (personId, categoryId) => {
      const categories = getters['getCategories'](personId)
      return categories.find(c => c.id === categoryId)
    },

    getCaveats: (_state, getters, rootState) => personId => {
      const person = getters.getPerson(personId)
      const contractPerson = rootState.contract.persons.find(p => p.contractNumber === person.contractNumber)
      return BasketHelper.getCaveatsForPerson(person, contractPerson)
    },

    getContractData: state => {
      return {
        address: state.address,
        contractStartDate: state.contractStartDate
      }
    },

    /**
     * get groups relevant for passed categoryId and personId
     * @param {String} personId
     * @param {String} categoryId
     * @return {Array}
     */
    getGroupsFromCategory: (_state, getters, _rootState, rootGetters) => (personId, categoryId) => {
      const groups = getters.getPerson(personId)?.products?.groups ?? {}

      return rootGetters['products/getGroupsFromCategory'](categoryId)
        .filter(group => !!groups[group.id])
        .map(group => Object.assign(
          group,
          groups[group.id]
        ))
        .sort((a, b) => (a.order > b.order) ? 1 : -1)
    },

    getPerson: state => personId => BasketHelper.getPersonWithAge(state.persons, personId),

    getPersons: state => cloneDeep(state.persons)
      .map((person, index) => {
        // only to read! don't depend on that to edit data
        person.number = index + 1
        person.age = DateTimeHelper.getAge(person.personData.dateOfBirth)
        return person
      }),

    /**
     * @param state
     * @param getters
     * @param _rootState
     * @param rootGetters
     * @return {*}
     */
    getPersonsForCart: (state, getters, _rootState, rootGetters) => {
      return PersonHelper.addTabInfoToPersons(cloneDeep(state.persons))
        .map(person => {
          person.caveats = getters.getCaveats(person.personId)
          person.selectedProducts = rootGetters['products/getSelectedProductsByCategory'](person.products.products)
          person.total = getters.getTotalPriceOfPerson(person.personId)
          return person
        })
    },

    getProduct: (_state, getters) => (personId, productId) =>
      getters.getPerson(personId).products.products[productId],

    getProductPrice: (_state, getters) => (personId, productId) =>
      getters.getProduct(personId, productId).prices.find(p => !!p.selected),

    getProductsFromCategory: (_state, getters, _rootState, rootGetters) => (personId, categoryId) => {
      const products = getters.getPerson(personId).products.products

      return rootGetters['products/getProductsFromCategory'](categoryId)
        .filter(product => !!products[product.id])
        .map(product => Object.assign(
          product,
          products[product.id]
        ))
        .sort((a, b) => (a.order > b.order) ? 1 : -1)
    },

    /**
     * get products relevant for passed groupid and personId
     * @param {String} personId
     * @param {String} groupId
     * @return {Array}
     */
    getProductsFromGroup: (_state, getters, _rootState, rootGetters) => (personId, groupId) => {
      const products = getters.getPerson(personId)?.products?.products ?? {}

      return rootGetters['products/getProductsFromGroup'](groupId)
        .filter(product => !!products[product.id])
        .map(product => Object.assign(
          product,
          products[product.id]
        ))
        .sort((a, b) => (a.order > b.order) ? 1 : -1)
    },

    getRecommendations: (_state, getters, rootState) => personId => {
      const productData = rootState['products']
      const person = getters.getPerson(personId)

      return BasketHelper.getRecommendations(productData, person)
    },

    getSelectedProductsOfPerson: (_state, getters, _rootState, rootGetters) => (personId) => {
      const products = rootGetters['products/getProducts'] || {}
      const person = getters.getPerson(personId)
      return ProductHelper.getSelectedProductsOfPerson(products, person?.products.products)
    },

    getTotalPriceOfPerson: (_state, getters) => (personId) => {
      const products = getters.getSelectedProductsOfPerson(personId)
      return BasketHelper.getTotalPriceOfPerson(products)
    },

    isProductInBasket: (_state, getters) => (personId, productId) => {
      const person = getters.getPerson(personId)
      return !!person.products.products[productId].selected
    },
  },

  mutations: {
    SET_BASKET (state, basket) {
      for (const key in basket) {
        // only update changed properties, maybe some further optimizations are in order?
        if (!isEqual(basket[key], state[key])) {
          Vue.set(state, key, basket[key])
        }
      }
    }
  },

  actions: {
    setBasket ({ _state, commit }, basket) {
      return new Promise((resolve) => {
        commit('SET_BASKET', basket)
        LogHelper.info(['%cSET_BASKET', 'color: lightgreen', basket])
        this._vm.$EventBus.$emit(EVENT_BASKET_UPDATED, basket)
        resolve(basket)
      })
    }
  }
}
